import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Filter } from 'lucide-react';
import { FlightCard } from '../flights/FlightCard';
import { Pagination } from '../../services/CarTripService';
import { FilterChipsInput } from '../common/FilterChipsInput';
import type { Flight, FlightsFilter } from '../../types/flight';
import type { TranslationKeys } from '../../translations';

const LoadingSpinner = () => (
    <div className="flex justify-center items-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
    </div>
);

export type FlightsTabPanelProps = {
    t: TranslationKeys;
    language: string;
    flights: Flight[];
    flightsLoading: boolean;
    flightsTotal: number;
    flightsPage: number;
    flightsPerPage: number;
    setFlightsPage: React.Dispatch<React.SetStateAction<number>>;
    flightsFilter: FlightsFilter | null;
    setFlightsFilter: (filter: FlightsFilter | null) => void;
    showFlightsFilter: boolean;
    setShowFlightsFilter: React.Dispatch<React.SetStateAction<boolean>>;
    flightTags: string[] | undefined;
    loadFlightTags?: () => void | Promise<void>;
    selectedFlightId: number | null;
    setSelectedFlightId: React.Dispatch<React.SetStateAction<number | null>>;
    editFlight: (flight: Flight) => void;
    deleteFlight: (flight: Flight) => void;
    viewFlight: (flight: Flight) => void;
    openNewFlightForm: () => void;
};

export const FlightsTabPanel: React.FC<FlightsTabPanelProps> = ({
    t,
    language,
    flights,
    flightsLoading,
    flightsTotal,
    flightsPage,
    flightsPerPage,
    setFlightsPage,
    flightsFilter,
    setFlightsFilter,
    showFlightsFilter,
    setShowFlightsFilter,
    flightTags,
    loadFlightTags,
    selectedFlightId,
    setSelectedFlightId,
    editFlight,
    deleteFlight,
    viewFlight,
    openNewFlightForm,
}) => {
    const [filterChips, setFilterChips] = useState<string[]>([]);
    const [intlFilterDraft, setIntlFilterDraft] = useState<'all' | 'intl' | 'domestic'>('all');

    useEffect(() => {
        if (!showFlightsFilter) return;
        void loadFlightTags?.();
        const f = flightsFilter;
        if (!f) {
            setFilterChips([]);
            setIntlFilterDraft('all');
            return;
        }
        const next: string[] = [];
        if (f.tags?.length) next.push(...f.tags);
        if (f.dateFrom?.slice(0, 4) && f.dateTo?.slice(0, 4) && f.dateFrom.slice(0, 4) === f.dateTo.slice(0, 4)) {
            next.push(f.dateFrom.slice(0, 4));
        } else if (f.dateFrom?.slice(0, 4)) {
            next.push(f.dateFrom.slice(0, 4));
        }
        if (f.q?.trim()) {
            f.q.trim().split(/\s+/).forEach((p: string) => {
                if (p && !next.includes(p)) next.push(p);
            });
        }
        setFilterChips(next);
        if (f.isInternational === true) setIntlFilterDraft('intl');
        else if (f.isInternational === false) setIntlFilterDraft('domestic');
        else setIntlFilterDraft('all');
    }, [showFlightsFilter, flightsFilter, loadFlightTags]);

    const yearSuggestions = useMemo(() => {
        const y = new Date().getFullYear();
        return [String(y), String(y - 1), String(y - 2), String(y - 3)];
    }, []);

    const filterSuggestions = useMemo(() => {
        const tags = Array.isArray(flightTags) ? flightTags : [];
        return [...yearSuggestions, ...tags].filter((v, i, a) => a.indexOf(v) === i);
    }, [flightTags, yearSuggestions]);

    const activeFilterCount =
        (flightsFilter?.tags?.length || 0) +
        (flightsFilter?.q?.trim() ? 1 : 0) +
        (flightsFilter?.dateFrom || flightsFilter?.dateTo ? 1 : 0) +
        (flightsFilter?.isInternational === true || flightsFilter?.isInternational === false ? 1 : 0);

    const applyFlightsFilter = () => {
        const tagSet = new Set((flightTags || []).map((t: string) => t.toLowerCase()));
        const years = filterChips.filter(c => /^\d{4}$/.test(c)).map(Number);
        const knownTags = filterChips.filter(c => !/^\d{4}$/.test(c) && tagSet.has(c.toLowerCase()));
        const freeText = filterChips.filter(c => !/^\d{4}$/.test(c) && !tagSet.has(c.toLowerCase()));
        const next: FlightsFilter = {};
        if (knownTags.length) next.tags = knownTags;
        if (freeText.length) next.q = freeText.join(' ');
        if (years.length) {
            next.dateFrom = `${Math.min(...years)}-01-01`;
            next.dateTo = `${Math.max(...years)}-12-31`;
        }
        if (intlFilterDraft === 'intl') next.isInternational = true;
        if (intlFilterDraft === 'domestic') next.isInternational = false;
        const empty = !next.tags?.length && !next.q && !next.dateFrom && next.isInternational == null;
        setFlightsFilter(empty ? null : next);
        setFlightsPage(1);
        setShowFlightsFilter(false);
    };

    const resetFlightsFilter = () => {
        setFlightsFilter(null);
        setFilterChips([]);
        setIntlFilterDraft('all');
        setFlightsPage(1);
    };

    return (
        <div className="space-y-2">
            <div className="mb-2 sm:mb-3 flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <button
                        type="button"
                        onClick={() => setShowFlightsFilter(!showFlightsFilter)}
                        className={`inline-flex items-center gap-1.5 sm:gap-2 min-h-9 px-2.5 sm:px-3 py-1.5 rounded-lg text-sm border transition-colors ${showFlightsFilter || activeFilterCount > 0 ? 'bg-sky-100 dark:bg-sky-900/40 border-sky-300 dark:border-sky-700 text-sky-800 dark:text-sky-200' : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                        title={t.filter}
                    >
                        <Filter className="h-4 w-4" />
                        <span className="hidden xs:inline">{t.filter}</span>
                        {activeFilterCount > 0 && <span className="text-xs bg-sky-600 text-white px-1.5 rounded">{activeFilterCount}</span>}
                    </button>
                    {(showFlightsFilter) && (
                        <div className="mt-2 p-3 rounded-2xl border border-sky-200/80 dark:border-slate-600 bg-gradient-to-br from-sky-50/90 to-white dark:from-slate-800/80 dark:to-slate-900/60 space-y-2.5">
                            <p className="text-[11px] text-stone-500 dark:text-slate-400">{t.filterChipsHint}</p>
                            <FilterChipsInput
                                chips={filterChips}
                                onChange={setFilterChips}
                                suggestions={filterSuggestions}
                                placeholder={t.filterChipsPlaceholder}
                            />
                            <div className="flex flex-wrap gap-1.5">
                                {([
                                    { id: 'all' as const, label: language?.startsWith('ru') ? 'Все' : 'All' },
                                    { id: 'intl' as const, label: t.internationalFlight },
                                    { id: 'domestic' as const, label: t.domesticFlight },
                                ]).map(opt => (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() => setIntlFilterDraft(opt.id)}
                                        className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                                            intlFilterDraft === opt.id
                                                ? 'bg-sky-600 text-white border-sky-600'
                                                : 'border-stone-300 dark:border-slate-600 text-stone-600 dark:text-slate-300 hover:bg-stone-100 dark:hover:bg-slate-700'
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={applyFlightsFilter}
                                    className="px-3.5 py-2 rounded-xl text-sm font-medium bg-sky-600 text-white hover:bg-sky-700 shadow-sm"
                                >
                                    {t.applyFilter}
                                </button>
                                <button
                                    type="button"
                                    onClick={resetFlightsFilter}
                                    className="px-3.5 py-2 rounded-xl text-sm border border-stone-300 dark:border-slate-600 text-stone-700 dark:text-slate-300 hover:bg-stone-100 dark:hover:bg-slate-700"
                                >
                                    {t.resetFilter}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => openNewFlightForm()}
                    className="hidden md:inline-flex items-center justify-center shrink-0 min-h-9 min-w-9 px-2.5 py-1.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-sky-500 to-cyan-600 hover:from-sky-600 hover:to-cyan-700 shadow-sm"
                    title={t.addNewFlight}
                    aria-label={t.addNewFlight}
                >
                    <Plus className="h-4 w-4" />
                </button>
            </div>
            {flightsLoading ? <LoadingSpinner /> : flights.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 gap-3 text-stone-500 dark:text-slate-400">
                    <p className="text-sm">{activeFilterCount > 0 ? (t.noFilterResults || 'Ничего не найдено') : (t.addFirstFlight || 'Добавьте первый рейс')}</p>
                    {activeFilterCount > 0 && (
                        <button type="button" onClick={resetFlightsFilter} className="text-sm text-sky-600 hover:underline">
                            {t.resetFilter}
                        </button>
                    )}
                </div>
            ) : (
                <>
                    {flights.map(f => (
                        <FlightCard
                            key={f.id}
                            flight={f}
                            selected={f.id === selectedFlightId}
                            onSelect={() => setSelectedFlightId(prev => (prev === f.id ? null : f.id))}
                            onView={viewFlight}
                            onEdit={editFlight}
                            onDelete={deleteFlight}
                        />
                    ))}
                    {flightsTotal > flightsPerPage && (
                        <Pagination
                            currentPage={flightsPage}
                            totalPages={Math.ceil(flightsTotal / flightsPerPage)}
                            onPageChange={setFlightsPage}
                            onPrevPage={() => setFlightsPage(p => Math.max(1, p - 1))}
                            onNextPage={() => setFlightsPage(p => Math.min(Math.ceil(flightsTotal / flightsPerPage), p + 1))}
                        />
                    )}
                </>
            )}
        </div>
    );
};
